// Pure logic for the Smart Timetable feature.
// Given a set of constraints it builds a full period-by-period bell schedule
// and flags conflicts (overlaps, missing breaks, time that doesn't fit, etc).
// Frontend-only — no backend involved.

export const SLOT_TYPES = {
    PERIOD: "PERIOD",
    MINI_BREAK: "MINI_BREAK",
    MAIN_BREAK: "MAIN_BREAK",
};

const MINI_BREAK_EVERY = 2; // insert a mini break after every N periods

export const parseTimeToMinutes = (value) => {
    const match = String(value || "").trim().match(/^(\d{1,2}):(\d{2})$/);
    if (!match) {
        return null;
    }
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (hours > 23 || minutes > 59) {
        return null;
    }
    return hours * 60 + minutes;
};

export const minutesToDisplay = (totalMinutes) => {
    const normalized = ((Math.round(totalMinutes) % (24 * 60)) + 24 * 60) % (24 * 60);
    let hours = Math.floor(normalized / 60);
    const minutes = String(normalized % 60).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    if (hours === 0) {
        hours = 12;
    }
    return `${hours}:${minutes} ${ampm}`;
};

export const minutesTo24h = (totalMinutes) => {
    const normalized = ((Math.round(totalMinutes) % (24 * 60)) + 24 * 60) % (24 * 60);
    const hours = String(Math.floor(normalized / 60)).padStart(2, "0");
    const minutes = String(normalized % 60).padStart(2, "0");
    return `${hours}:${minutes}`;
};

// Validate raw constraints. Returns an array of {level, message}.
const validateConfig = (config) => {
    const issues = [];
    const start = parseTimeToMinutes(config.startHour);
    const end = parseTimeToMinutes(config.endHour);
    const periodDuration = Number(config.periodDuration);
    const names = (config.subjects || []).map((name) => String(name).trim()).filter(Boolean);

    if (start === null) {
        issues.push({level: "error", message: "Start hour is missing or invalid."});
    }
    if (end === null) {
        issues.push({level: "error", message: "End hour is missing or invalid."});
    }
    if (start !== null && end !== null && end <= start) {
        issues.push({level: "error", message: "End hour must be after start hour."});
    }
    if (!periodDuration || periodDuration <= 0) {
        issues.push({level: "error", message: "Period duration must be greater than zero."});
    }
    if (names.length === 0) {
        issues.push({level: "error", message: "Add at least one subject."});
    }
    if (start !== null && end !== null && periodDuration > 0 && end - start < periodDuration) {
        issues.push({level: "error", message: "The time range is shorter than a single period."});
    }
    return issues;
};

// Detect conflicts in an already-built slot list.
const detectConflicts = (slots, context) => {
    const conflicts = [];

    for (let i = 1; i < slots.length; i += 1) {
        if (slots[i].start < slots[i - 1].end) {
            conflicts.push({
                level: "error",
                message: `Overlap between "${slots[i - 1].label}" and "${slots[i].label}".`,
            });
        }
    }

    // Long run of periods without any break.
    let consecutivePeriods = 0;
    slots.forEach((slot) => {
        if (slot.type === SLOT_TYPES.PERIOD) {
            consecutivePeriods += 1;
            if (consecutivePeriods === 5) {
                conflicts.push({
                    level: "warning",
                    message: "5 or more periods run without a break — students may need a rest.",
                });
            }
        } else {
            consecutivePeriods = 0;
        }
    });

    if (context.mainBreakRequested && !context.mainBreakPlaced) {
        conflicts.push({
            level: "warning",
            message: "Main break could not fit in the available time and was skipped.",
        });
    }
    if (context.miniBreakRequested && !context.miniBreakPlaced) {
        conflicts.push({
            level: "warning",
            message: "Mini break could not fit between periods and was skipped.",
        });
    }

    const lastSlot = slots[slots.length - 1];
    if (lastSlot && context.end - lastSlot.end >= context.periodDuration) {
        conflicts.push({
            level: "info",
            message: `${context.end - lastSlot.end} minute(s) left unused before the end hour.`,
        });
    }

    if (slots.filter((slot) => slot.type === SLOT_TYPES.PERIOD).length === 0) {
        conflicts.push({level: "error", message: "No periods could be scheduled in the given time."});
    }

    return conflicts;
};

export const generateTimetable = (config) => {
    const validation = validateConfig(config);
    if (validation.some((issue) => issue.level === "error")) {
        return {slots: [], conflicts: validation, summary: null};
    }

    const start = parseTimeToMinutes(config.startHour);
    const end = parseTimeToMinutes(config.endHour);
    const periodDuration = Number(config.periodDuration);
    const miniBreak = Number(config.miniBreak) || 0;
    const mainBreak = Number(config.mainBreak) || 0;
    const names = (config.subjects || []).map((name) => String(name).trim()).filter(Boolean);

    const slots = [];
    const midpoint = start + Math.floor((end - start) / 2);
    let cursor = start;
    let subjectIndex = 0;
    let periodsSinceBreak = 0;
    let mainBreakPlaced = false;
    let miniBreakPlaced = false;

    while (cursor + periodDuration <= end) {
        slots.push({
            type: SLOT_TYPES.PERIOD,
            label: names[subjectIndex % names.length],
            start: cursor,
            end: cursor + periodDuration,
        });
        cursor += periodDuration;
        subjectIndex += 1;
        periodsSinceBreak += 1;

        // Only consider a break if another period can still follow it.
        if (cursor + periodDuration > end) {
            break;
        }

        if (mainBreak > 0 && !mainBreakPlaced && cursor >= midpoint && cursor + mainBreak + periodDuration <= end) {
            slots.push({type: SLOT_TYPES.MAIN_BREAK, label: "Main Break", start: cursor, end: cursor + mainBreak});
            cursor += mainBreak;
            mainBreakPlaced = true;
            periodsSinceBreak = 0;
            continue;
        }

        if (miniBreak > 0 && periodsSinceBreak >= MINI_BREAK_EVERY && cursor + miniBreak + periodDuration <= end) {
            slots.push({type: SLOT_TYPES.MINI_BREAK, label: "Mini Break", start: cursor, end: cursor + miniBreak});
            cursor += miniBreak;
            miniBreakPlaced = true;
            periodsSinceBreak = 0;
        }
    }

    const conflicts = detectConflicts(slots, {
        end,
        periodDuration,
        mainBreakRequested: mainBreak > 0,
        mainBreakPlaced,
        miniBreakRequested: miniBreak > 0,
        miniBreakPlaced,
    });

    const periodCount = slots.filter((slot) => slot.type === SLOT_TYPES.PERIOD).length;
    const breakCount = slots.length - periodCount;

    const summary = {
        periodCount,
        breakCount,
        startMinutes: start,
        endMinutes: slots.length ? slots[slots.length - 1].end : start,
        totalMinutes: (slots.length ? slots[slots.length - 1].end : start) - start,
    };

    return {slots, conflicts, summary};
};
